import Group from 'components/utils/Group'
import { rem } from 'polished'
import React, { useContext, useMemo } from 'react'
import styled from 'styled-components'
import BodyText from 'components/Luxkit/Typography/BodyText'
import BestOfBackgroundImage from './BestOfBackgroundImage'
import BestOfSquareLogo from './BestOfSquareLogo'
import TextButton from 'components/Luxkit/Button/TextButton'
import { matchPath, useRouteMatch } from 'react-router'
import useOffer from 'hooks/Offers/useOffer'
import { useAppSelector } from 'hooks/reduxHooks'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { encodeSearchParams } from 'lib/search/searchUtils'
import config from 'constants/config'
import { BestOfCampaignRegionTags } from './BestOfConstants'
import GeoContext from 'contexts/geoContext'
import { useQsQueryParams } from 'hooks/useQueryParams'

const Background = styled(BestOfBackgroundImage)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
`

const Content = styled(Group)`
  transform: translateZ(0);
`

const Banner = styled.div`
  padding: ${rem(12)} ${rem(16)};
  position: relative;
`

const offerPageRoutes: Array<string> = [
  '/:regionCode/offer/:slug/:offerId',
  '/:regionCode/cruises/:slug/:offerId',
  '/:regionCode/tour/:offerId',
]

const noBannerRoutes: Array<string> = [
  '/:regionCode/search',
  '/:regionCode/checkout',
]

function BestOfPageBanner() {
  const match = useRouteMatch<{offerId: string}>(offerPageRoutes)
  const queryParams = useQsQueryParams<{ campaigns?: Array<string> }>()
  const { currentRegionCode } = useContext(GeoContext)
  const [offer] = useOffer(match?.params.offerId)
  const currentPath = useAppSelector(state => state.router.location.pathname)
  const hide = useMemo((): boolean => {
    const queryContainsCampaignTag = queryParams.campaigns?.includes(BestOfCampaignRegionTags[currentRegionCode])
    // hide on:
    // - the home page
    // - any offer page where the offer is a best of offer
    // - the best of search results page
    return !!offer?.isBestOf2024 ||
    !!matchPath(currentPath, { path: '/:regionCode', exact: true }) ||
    !!(matchPath(currentPath, { path: noBannerRoutes }) && queryContainsCampaignTag)
  }, [queryParams.campaigns, currentRegionCode, offer?.isBestOf2024, currentPath])

  const searchQuery = useMemo(() => encodeSearchParams({
    searchItem: ANYWHERE_SEARCH_ITEM,
    rooms: [config.search.defaultOccupants],
    filters: {
      campaigns: [BestOfCampaignRegionTags[currentRegionCode]],
    },
  }), [currentRegionCode])

  if (hide) {
    return null
  }

  return <Banner>
    <Background overlayStyle="default"/>
    <Content
      direction="horizontal"
      gap={12}
      verticalAlign="center"
      horizontalAlign="center"
  >
      <BestOfSquareLogo height={40} />
      <BodyText variant="medium" colour="neutral-eight" weight="bold">
        The best escapes are <u>back</u>. Limited time only
      </BodyText>
      <TextButton
        kind="secondary"
        variant="ghost"
        size="small"
        to={`/search?${searchQuery.toString()}`}
      >
        View offers
      </TextButton>
    </Content>
  </Banner>
}

export default BestOfPageBanner
